/**
 * Folders of the media library. The API returns them as a flat list; these
 * helpers build breadcrumbs and trees from it and guard against cycles.
 */
export interface FolderNode {
  id: string;
  parentId: string | null;
  name: string;
}

export const FOLDER_NAME_MAX_LENGTH = 120;

/** Label of the top level (breadcrumb root, "not in a folder"). */
export const MEDIA_ROOT_LABEL = "Mediathek";

/** Folders from the top level down to `folderId` (inclusive). Empty for the top level. */
export function folderPath<T extends FolderNode>(
  folders: readonly T[],
  folderId: string | null | undefined,
): T[] {
  const byId = new Map(folders.map((folder) => [folder.id, folder]));
  const path: T[] = [];
  const seen = new Set<string>();
  let current = folderId ? byId.get(folderId) : undefined;
  while (current && !seen.has(current.id)) {
    seen.add(current.id);
    path.unshift(current);
    current = current.parentId ? byId.get(current.parentId) : undefined;
  }
  return path;
}

/** The folder itself and every folder below it. */
export function folderSubtree(folders: readonly FolderNode[], folderId: string): Set<string> {
  const children = new Map<string, string[]>();
  for (const folder of folders) {
    if (!folder.parentId) continue;
    const list = children.get(folder.parentId) ?? [];
    list.push(folder.id);
    children.set(folder.parentId, list);
  }
  const result = new Set<string>([folderId]);
  const queue = [folderId];
  while (queue.length > 0) {
    for (const child of children.get(queue.pop()!) ?? []) {
      if (result.has(child)) continue;
      result.add(child);
      queue.push(child);
    }
  }
  return result;
}

/** Moving a folder into itself or into one of its subfolders would detach it from the tree. */
export function wouldCreateCycle(
  folders: readonly FolderNode[],
  folderId: string,
  parentId: string | null,
): boolean {
  return parentId !== null && folderSubtree(folders, folderId).has(parentId);
}

const byName = (a: FolderNode, b: FolderNode) =>
  a.name.localeCompare(b.name, "de", { sensitivity: "base", numeric: true });

/** Direct subfolders, sorted by name. */
export function childFolders<T extends FolderNode>(
  folders: readonly T[],
  parentId: string | null,
): T[] {
  return folders.filter((folder) => folder.parentId === parentId).sort(byName);
}

/** All folders depth-first and sorted by name, with their depth (for folder choosers). */
export function folderTree<T extends FolderNode>(
  folders: readonly T[],
): { folder: T; depth: number }[] {
  const result: { folder: T; depth: number }[] = [];
  const seen = new Set<string>();
  const visit = (parentId: string | null, depth: number) => {
    for (const folder of childFolders(folders, parentId)) {
      if (seen.has(folder.id)) continue;
      seen.add(folder.id);
      result.push({ folder, depth });
      visit(folder.id, depth + 1);
    }
  };
  visit(null, 0);
  return result;
}

/**
 * `name`, or "name (2)", "name (3)" … if a sibling already uses it (ignoring case),
 * e.g. for subfolders that move up when their folder is deleted.
 */
export function uniqueFolderName(name: string, taken: Iterable<string>): string {
  const used = new Set([...taken].map((entry) => entry.toLowerCase()));
  if (!used.has(name.toLowerCase())) return name;
  for (let n = 2; ; n++) {
    const suffix = ` (${n})`;
    const candidate = `${name.slice(0, FOLDER_NAME_MAX_LENGTH - suffix.length).trimEnd()}${suffix}`;
    if (!used.has(candidate.toLowerCase())) return candidate;
  }
}

/** "Mediathek › Kampagne › Reels" */
export function folderLabel(folders: readonly FolderNode[], folderId: string | null): string {
  return [MEDIA_ROOT_LABEL, ...folderPath(folders, folderId).map((folder) => folder.name)].join(
    " › ",
  );
}
